import { Button } from "@mui/material";
import CloseIcon from '@mui/icons-material/Close';
import styles from "./index.module.scss";
import React from "react";
import GeneralDialog from "../../core/GeneralDialog";
import { ProcessFeeDetailsResponseData, ProcessFeeDetailsResponseDataOption, ProcessFeeOption } from "../../../apis/models/ProcessFeeDetailsResponse";
import {formatCurrency, formatNumber, str2number} from "../../../libs/helpers";
import DateCell from "../../core/DateCell";
import InputCell from "../../core/InputCell";
import { ProcessFeeOptionRequestItem } from "../../../apis/models/ProcessFeeRequest";
import ProcessFeeDetailsDialog from "../../dialogs/invoiceDetails/ProcessFeeDetailsDialog";
import { InvoiceMasterData } from "../../../apis/models/InvoiceMasterRequest";
import { InvStatus } from "../../../libs/constants";
import ProcessFeeOptionSelectCell from "../../core/ProcessFeeOptionSelectCell";
import DateWithoutTime from "../../../libs/dateWithoutTime";
import UnfoldMoreIcon from '@mui/icons-material/UnfoldMore';

type InputField = {
  uniqueId: string;
  dateAdded: DateWithoutTime,
  productNumber: string,
  workDetailOption?: ProcessFeeDetailsResponseDataOption,
  quantity: string,
  unitPrice: string,
}

type ErrorInfo = {
  dateAdded?: string;
  workDetails?: string;
  quantity?: string;
}

export type ProcessFeeTabType = InputField & {
  error: ErrorInfo
}

const ProcessFeeDetails = (props: ProcessFeeDetailsProps) => {
  const [openWarningDialog, setOpenWarningDialog] = React.useState<boolean>(false);
  const [openAddDialog, setOpenAddDialog] = React.useState<boolean>(false);

  const handleDateChange = (date: Date | null, uniqueId: string) => {
    const newItem = props.tabValues.map(fee => {
      if (uniqueId === fee.uniqueId) {
        fee.dateAdded = new DateWithoutTime(date);
      }
      fee.error.dateAdded = validateDateAdded(fee.dateAdded)
      return fee;
    })
    props.onChange(newItem);
  }

  const handleProductChange = (value: string | number, uniqueId: string) => {
    const newItem = props.tabValues.map(fee => {
      if (uniqueId === fee.uniqueId) {
        fee.productNumber = String(value);
      }
      return fee;
    })
    props.onChange(newItem);
  }

  const handleSelectOption = (option: ProcessFeeOption | undefined, uniqueId: string) => {
    const newItem = props.tabValues.map(fee => {
      if (uniqueId === fee.uniqueId) {
        fee.workDetailOption = option?.processFeeOption;
        fee.unitPrice = option ? String(option.processFeeOption.unitPrice) : "";
      }
      fee.error.workDetails = fee.workDetailOption ? undefined : "作業内容を選択してください";
      return fee;
    })
    props.onChange(newItem);
  }

  const handleQuantityChange = (value: string | number, uniqueId: string) => {
    const newItem = props.tabValues.map(fee => {
      if (uniqueId === fee.uniqueId) {
        fee.quantity = String(value);
      }
      fee.error.quantity = validateNumber(fee.quantity);
      return fee;
    })
    props.onChange(newItem);
  }

  const onDelete = (uniqueId: string) => {
    props.onChange(props.tabValues.filter(rowItem => rowItem.uniqueId !== uniqueId));
  }

  const addProcessFeeDetails = (items: ProcessFeeDetailsResponseData[]) => {
    const uniquePrefix = `dialog-${new Date().getTime()}`;

    const addItems = items.map((item, index) => {
      const uniqueId = `${uniquePrefix}-${index}`;

      return {
        uniqueId: uniqueId,
        dateAdded: new DateWithoutTime(item.dateAdded),
        productNumber: item.productNumber,
        quantity: String(item.quantity),
        unitPrice: String(item.processFeeOption.unitPrice),
        workDetailOption: item.processFeeOption,
        error: {}
      } as ProcessFeeTabType
    })
    props.onChange(props.tabValues.concat(addItems));
    setOpenAddDialog(false);
  }

  const isConfirmed = React.useMemo(() => {
    return props.targetInvoice?.status === InvStatus.CONFIRMED || props.targetInvoice?.status === InvStatus.EXPORT;
  }, [props.targetInvoice])

  const rowTotal = (unitPrice: string, quantity: string) => {
    return str2number(unitPrice) * str2number(quantity);
  }

  const total = React.useMemo(() => {
    return props.tabValues.reduce((prev, fee) => {
      return prev + str2number(fee.quantity) * str2number(fee.unitPrice)
    }
    , 0)
  }, [props.tabValues])

  const validateDateAdded = (value: DateWithoutTime): string | undefined => {
    const dateString = value.getValue4Display()
    if (value.getValue4Display().length === 0) {
      return "入力必須です"
    } else if (value.isInvalid()) {
      return "年月日で入力してください"
    } else {
      const invoiceFrom = new DateWithoutTime(props.targetInvoice.from)
      const invoiceTo = new DateWithoutTime(props.targetInvoice.to)
      if (dateString < invoiceFrom.getValue4Display() || invoiceTo.getValue4Display() < dateString) {
        return "請求期間の範囲で入力してください"
      }
    }
    return undefined
  }

  const validateNumber = (value: string) => {
    if (value.length === 0) {
      return "入力必須です"
    } else if (isNaN(+value) || parseInt(value) < 1) {
      return "１以上を入力してください"
    }
    return undefined
  }

  const hasError = React.useMemo(() => {
    return props.tabValues.some(fee => {
      return fee.error.dateAdded || fee.error.quantity || fee.error.workDetails
    })
  },[props.tabValues]);

  return (<>
    <div className={styles.container}>
      <div className={styles.statistics}>
        <div>
          合計金額：<span>{formatCurrency(total)}</span>
        </div>
        {
          isConfirmed ? 
          <div className={styles.download_section}>
            <Button variant="outlined" color="primary" onClick={() => setOpenWarningDialog(true)}>編集</Button>
            {/* <Button variant="contained" color="primary" onClick={onDownloadReport}>加工費報告書ダウンロード</Button> */}
          </div> : <></>
        }
      </div>
      <div className={styles.table}>
        <div className={isConfirmed ? `${styles.table__head}` : `${styles.table__head} ${styles.unconfirmed_head}`}>
          <div className={styles.sort} onClick={() => props.onClickSort()}>
            日付
            <UnfoldMoreIcon />
          </div>
          <div>品番</div>
          <div>作業内容</div>
          <div>単価</div>
          <div>枚数</div>
          <div>作業費</div>
          {!isConfirmed ? <div></div> : <></>}
        </div>
        {
          props.tabValues.map((item, index) => (
            <div className={isConfirmed ? `${styles.table__row}` : `${styles.table__row} ${styles.unconfirmed_row}`} key={item.uniqueId}>
              <DateCell 
                isConfirmed={isConfirmed} 
                onChangeDate={(val) => handleDateChange(val, item.uniqueId)} 
                value={item.dateAdded.getValue4Display()}
              />
              <InputCell
                isConfirmed={isConfirmed}
                onChangeText={(val) => handleProductChange(val, item.uniqueId)}
                type="text"
                value={item.productNumber}
                textAlignment="left"
              />
              <ProcessFeeOptionSelectCell
                options={props.processFeeOptions}
                onSelect={(value) => handleSelectOption(value, item.uniqueId)}
                onAddOption={(value) => props.onAddOptions(value)}
                isConfirmed={isConfirmed}
                value={item.workDetailOption?.workDetails ?? ""}
                error={undefined}
              />
              <div
                className={isConfirmed ? styles.table__row_price : `${styles.table__row_price} ${styles.disabled}`}
              >
                {formatNumber(Number(item.unitPrice))}
              </div>
              <InputCell
                isConfirmed={isConfirmed}
                onChangeText={(val) => handleQuantityChange(val, item.uniqueId)}
                type="number"
                value={item.quantity}
                textAlignment="right"
              />
              <div
                className={isConfirmed ? styles.table__row_amount : `${styles.table__row_amount} ${styles.disabled}`}
              >
                {formatNumber(rowTotal(item.unitPrice, item.quantity))}
              </div>
              {
                !isConfirmed ?
                <div className={styles.existing_delete}>
                  <CloseIcon color="primary" className={styles.delete_icon} onClick={() => onDelete(item.uniqueId)}/>
                </div> : <></>
              }
            </div>
          ))
        }
      </div>
      {/* edit button, only when invoice was NOT confirmed  ↓ */}
      {
        !isConfirmed ?
        <div className={styles.add_feature}>
          <Button color="primary" variant="outlined" onClick={() => setOpenAddDialog(true)}>＋項目追加</Button>
        </div> : <></>
      }
    </div>
    {/* dialog to warn user when they try to edit a confirmed invoice  ↓ */}
    <GeneralDialog 
      open={openWarningDialog} 
      onCancel={() => setOpenWarningDialog(false)} 
      cancelText="キャンセル" 
      onSave={() => {props.onNotYetConfirmed(); setOpenWarningDialog(false)}} 
      saveText="確定を取り消す" 
      title="請求確定取り消し" 
      onClose={() => setOpenWarningDialog(false)}
    >
      <div>
      既に確定済の請求です。<br/>
      内容を編集するには請求の確定取り消しが必要です。<br/><br/>
      </div>
      <div>請求の確定を取り消しますか？</div>
    </GeneralDialog>
    {
      !isConfirmed ?
      <div className={styles.functions}>
        <Button variant="outlined" color="primary" onClick={() => props.onReset()} disabled={!props.isChanged}>編集内容を破棄</Button>
        <Button variant="contained" color="secondary" onClick={() => props.onSave()} disabled={hasError || !props.isChanged}>保存</Button>
      </div> : <></>
    }
    {
      openAddDialog ? (
        <ProcessFeeDetailsDialog
          targetBrand={props.targetInvoice.brand}
          targetInvoice={props.targetInvoice}
          processFeeOptions={props.processFeeOptions}
          onClose={() => {setOpenAddDialog(false)}}
          onCancel={() => {setOpenAddDialog(false)}}
          open={openAddDialog}
          onSave={(items) => addProcessFeeDetails(items)}
          onAddOptions={(val) => props.onAddOptions(val)}
        />
      ) : (
        <></>
      )
    }
  </>);
};

export default ProcessFeeDetails;

export type ProcessFeeDetailsProps = {
  targetInvoice: InvoiceMasterData,
  tabValues: ProcessFeeTabType[];
  processFeeOptions: ProcessFeeOption[];
  isChanged: boolean;
  onDownloadReport: () => void;
  onNotYetConfirmed: () => void;
  onReset: () => void;
  onSave: () => void;
  onChange: (val: ProcessFeeTabType[]) => void;
  onClickSort: () => void;
  onAddOptions: (val: ProcessFeeOptionRequestItem) => void
};
